import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { CameraIcon, PhotoIcon, FolderIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { nutritionAPI } from '../../services/api'
import { isAcceptedFoodPhoto, PHOTO_ACCEPT_ATTR } from '../../utils/foodPhotoTypes'

const FoodPhotoUpload = forwardRef(({
  onAnalysisComplete,
  onAnalysisStart,
  onAnalysisError,
  onCancel,
  // VH-165: start straight in one source and never show the chooser. The phone
  // block opens the camera on tap, the way CaptureFlow does for workouts —
  // picking a source first was the friction, and `capture="environment"` is
  // also what keeps the OS from writing a copy to the camera roll.
  //
  // This stays a PROP on the existing component rather than a second, smaller
  // capture component: one upload and analysis path entered two ways. A second
  // builder drifting from the first is what produced VH-139's field-shape bug.
  autoStartMode = null,
}, ref) => {
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState([]) // For batch processing
  const [currentFileIndex, setCurrentFileIndex] = useState(0)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [uploadMode, setUploadMode] = useState(autoStartMode) // 'camera' | 'file' | 'folder'
  // VH-205: an optional note the user writes BEFORE the first analysis — the
  // things a photo cannot show ("no sugar, made with stevia and walnut").
  // The backend has accepted `user_hint` since the re-analyse loop shipped;
  // the only place to type one was the review screen, i.e. after a wrong
  // answer had already been produced and paid for.
  const [hint, setHint] = useState('')
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)

  // Expose handleNextFile to parent
  // Opening the OS picker must happen in an effect, not during render.
  useEffect(() => {
    if (!autoStartMode) return
    const input = autoStartMode === 'folder' ? folderInputRef.current : fileInputRef.current
    input?.click()
  }, [autoStartMode])

  useImperativeHandle(ref, () => ({
    handleNextFile
  }))

  // Mutation for photo analysis
  const analyzeMutation = useMutation({
    mutationFn: ({ formData, hint: userHint }) =>
      nutritionAPI.analyzeFoodPhoto(formData, userHint || null),
    onError: (error) => {
      console.error('Photo analysis failed:', error)
      // VH-173: the PARENT owns `isAnalyzing` and renders the blocking overlay.
      // Failing without telling it left the spinner up forever with the upload
      // component hidden behind it — reported from UAT as "blocks the screen
      // completely, no way to interrupt". An alert() is not a state transition.
      onAnalysisError?.(error)
    }
  })

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Store the file for potential re-analysis
      setSelectedFiles([file])
      setCurrentFileIndex(0)
      processFile(file)
    }
  }

  // Handle folder selection
  const handleFolderSelect = (event) => {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      // Filter for valid image files
      const imageFiles = files.filter(isAcceptedFoodPhoto)

      if (imageFiles.length === 0) {
        alert(t('nutrition.errors.noValidImages'))
        return
      }

      // Store all files for batch processing
      setSelectedFiles(imageFiles)
      setCurrentFileIndex(0)

      // Process first file - pass the full batch array directly (don't rely on state)
      processFile(imageFiles[0], 0, imageFiles)
    }
  }

  // Process selected file - validates, creates preview, and auto-triggers analysis
  // batchFiles param allows passing the full array directly (avoids React state timing issues)
  const processFile = (file, fileIndex = 0, batchFiles = null) => {
    console.log('[FoodPhotoUpload] Processing file:', file.name, file.type, file.size)

    // Validate file type — by MIME *or* extension; see isAcceptedFoodPhoto.
    if (!isAcceptedFoodPhoto(file)) {
      console.error('[FoodPhotoUpload] Invalid file type:', file.type, file.name)
      alert(t('nutrition.errors.invalidFileType'))
      return
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      console.error('[FoodPhotoUpload] File too large:', file.size)
      alert(t('nutrition.errors.fileTooLarge'))
      return
    }

    console.log('[FoodPhotoUpload] File validated, setting selectedFile')
    setSelectedFile(file)

    // Create preview URL
    console.log('[FoodPhotoUpload] Creating preview URL')
    const reader = new FileReader()
    reader.onloadend = () => {
      console.log('[FoodPhotoUpload] Preview URL created, setting previewUrl')
      setPreviewUrl(reader.result)
    }
    reader.onerror = (error) => {
      console.error('[FoodPhotoUpload] FileReader error:', error)
    }
    reader.readAsDataURL(file)

    // VH-205: a single photo now STOPS here, at the preview, so the note
    // field below is reachable. A folder batch still runs straight through —
    // pausing on every one of twenty photos to ask for a note would make the
    // batch path useless, and it is a desktop testing tool, not the phone
    // habit this note exists for.
    const files = batchFiles || selectedFiles
    if (files.length > 1) {
      console.log('[FoodPhotoUpload] Auto-triggering analysis for:', file.name)
      const formData = new FormData()
      formData.append('file', file)
      analyzeFile(formData, fileIndex, batchFiles)
    }
  }

  // Handle upload and analysis
  const handleAnalyze = () => {
    if (!selectedFile) return

    const formData = new FormData()
    formData.append('file', selectedFile)

    analyzeFile(formData, null, null, hint)
  }

  // Handle next file in batch
  const handleNextFile = () => {
    if (currentFileIndex < selectedFiles.length - 1) {
      const nextIndex = currentFileIndex + 1
      setCurrentFileIndex(nextIndex)
      const nextFile = selectedFiles[nextIndex]

      // Process and analyze the next file automatically. processFile runs the
      // analysis itself for a batch, so the index has to be passed in — the
      // old code left it at the default 0 and then fired a SECOND analysis
      // from a setTimeout to correct it, uploading every photo twice.
      processFile(nextFile, nextIndex, selectedFiles)
    }
  }

  // Separate analyze function to avoid duplication
  // batchFiles param allows passing the full array directly (avoids React state timing issues)
  const analyzeFile = (formData, fileIndex = null, batchFiles = null, userHint = '') => {
    // Use provided index or current state
    const actualIndex = fileIndex !== null ? fileIndex : currentFileIndex
    // Use provided batch files or fall back to state
    const filesArray = batchFiles || selectedFiles

    // Notify parent that analysis is starting (for consistent loading UI)
    if (onAnalysisStart) {
      onAnalysisStart(filesArray, actualIndex)
    }

    analyzeMutation.mutate({ formData, hint: userHint }, {
      onSuccess: (response) => {
        // Add batch info if more files remain
        if (filesArray.length > 1 && actualIndex < filesArray.length - 1) {
          response.data.batchInfo = {
            currentIndex: actualIndex + 1,
            totalFiles: filesArray.length,
            hasMore: true
          }
        }

        // Call parent onAnalysisComplete with batch info
        if (onAnalysisComplete) {
          // Pass the files and index to parent so it can manage batch processing
          onAnalysisComplete(response.data, filesArray, actualIndex)
        }
      }
    })
  }

  // Clear selection
  const handleClear = () => {
    setSelectedFile(null)
    setSelectedFiles([])
    setCurrentFileIndex(0)
    setPreviewUrl(null)
    setUploadMode(null)
    setHint('')
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (folderInputRef.current) folderInputRef.current.value = ''
  }

  return (
    <div className="space-y-4">
      {/* Upload Mode Selection */}
      {!uploadMode && !autoStartMode && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Camera Option */}
          <button
            onClick={() => {
              setUploadMode('camera')
              fileInputRef.current?.click()
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">{t('nutrition.photoUpload.takePhoto')}</span>
            <span className="text-xs text-gray-500 mt-1">{t('nutrition.photoUpload.useCamera')}</span>
          </button>

          {/* File Upload Option */}
          <button
            onClick={() => {
              setUploadMode('file')
              fileInputRef.current?.click()
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <PhotoIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">{t('nutrition.photoUpload.uploadPhoto')}</span>
            <span className="text-xs text-gray-500 mt-1">{t('nutrition.photoUpload.chooseFromDevice')}</span>
          </button>

          {/* Folder Upload Option */}
          <button
            onClick={() => {
              setUploadMode('folder')
              folderInputRef.current?.click()
            }}
            className="hidden lg:flex flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <FolderIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">{t('nutrition.photoUpload.uploadFolder')}</span>
            <span className="text-xs text-gray-500 mt-1">{t('nutrition.photoUpload.forTesting')}</span>
          </button>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_ACCEPT_ATTR}
        capture={uploadMode === 'camera' ? 'environment' : undefined}
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={folderInputRef}
        type="file"
        accept={PHOTO_ACCEPT_ATTR}
        multiple
        webkitdirectory=""
        directory=""
        onChange={handleFolderSelect}
        className="hidden"
      />

      {/* Loading state during batch processing */}
      {uploadMode && !previewUrl && (
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-gray-300 rounded-lg">
          <svg className="animate-spin h-12 w-12 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">{t('nutrition.photoUpload.loadingPhoto')}</p>
        </div>
      )}

      {/* Preview and Analysis */}
      {previewUrl && (
        <div className="space-y-4">
          <div className="relative">
            <img
              src={previewUrl}
              alt="Food preview"
              className="w-full max-h-96 object-contain rounded-lg border-2 border-gray-200"
            />
            <button
              onClick={handleClear}
              className="absolute top-2 right-2 p-2 bg-surface-container rounded-full shadow-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p className="font-medium">{selectedFile?.name}</p>
              <p className="text-xs text-gray-500">
                {(selectedFile?.size / 1024).toFixed(1)} KB
              </p>
              {selectedFiles.length > 1 && (
                <p className="text-xs text-indigo-600 font-medium mt-1">
                  {t('nutrition.photoUpload.photoOf', { current: currentFileIndex + 1, total: selectedFiles.length })}
                </p>
              )}
            </div>
          </div>

          {/* VH-205: the note, before the analysis rather than after it. */}
          {selectedFiles.length <= 1 && (
            <div>
              <label
                htmlFor="food-photo-hint"
                className="block text-sm font-medium text-gray-900 mb-1"
              >
                {t('nutrition.photoUpload.addNote')}
              </label>
              <textarea
                id="food-photo-hint"
                rows={2}
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                disabled={analyzeMutation.isPending}
                placeholder={t('nutrition.photoUpload.notePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 placeholder-gray-400 disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('nutrition.photoUpload.noteHelp')}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleAnalyze}
              disabled={analyzeMutation.isPending}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {analyzeMutation.isPending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('nutrition.photoUpload.analyzing')}
                </>
              ) : (
                t('nutrition.photoUpload.analyzeFood')
              )}
            </button>
            <button
              onClick={handleClear}
              disabled={analyzeMutation.isPending}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('nutrition.photoUpload.chooseDifferent')}
            </button>
          </div>
        </div>
      )}

      {/* Cancel Button */}
      {onCancel && (
        <div className="flex justify-end">
          <button
            onClick={onCancel}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {t('nutrition.photoUpload.cancel')}
          </button>
        </div>
      )}
    </div>
  )
})

FoodPhotoUpload.displayName = 'FoodPhotoUpload'

export default FoodPhotoUpload
